import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { type ThemeMode } from './tokens';

const STORAGE_KEY = 'ielect-theme-mode';

interface ThemeContextValue {
	mode: ThemeMode;
	resolvedTheme: 'light' | 'dark';
	setMode: (mode: ThemeMode) => void;
	toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function getStoredTheme(): ThemeMode {
	if (typeof window === 'undefined') return 'system';
	const stored = localStorage.getItem(STORAGE_KEY);
	if (stored === 'light' || stored === 'dark' || stored === 'system') {
		return stored;
	}
	return 'system';
}

function getSystemTheme(): 'light' | 'dark' {
	if (typeof window === 'undefined') return 'light';
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [mode, setModeState] = useState<ThemeMode>(() => getStoredTheme());
	const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => getSystemTheme());

	// Listen for system theme changes
	useEffect(() => {
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

		const handleChange = (e: MediaQueryListEvent) => {
			setSystemTheme(e.matches ? 'dark' : 'light');
		};

		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);

	// Resolve the actual theme based on mode
	const resolvedTheme = useMemo(() => {
		if (mode === 'system') {
			return systemTheme;
		}
		return mode;
	}, [mode, systemTheme]);

	// Apply theme to document
	useEffect(() => {
		const root = document.documentElement;
		root.setAttribute('data-theme', resolvedTheme);

		// Also set color-scheme for native form controls
		root.style.colorScheme = resolvedTheme;
	}, [resolvedTheme]);

	// Persist theme mode
	const setMode = useCallback((newMode: ThemeMode) => {
		setModeState(newMode);
		localStorage.setItem(STORAGE_KEY, newMode);
	}, []);

	// Toggle between light and dark (skips system)
	const toggleTheme = useCallback(() => {
		setMode(resolvedTheme === 'light' ? 'dark' : 'light');
	}, [resolvedTheme, setMode]);

	const value = useMemo(
		() => ({
			mode,
			resolvedTheme,
			setMode,
			toggleTheme,
		}),
		[mode, resolvedTheme, setMode, toggleTheme]
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
	const context = useContext(ThemeContext);
	if (context === undefined) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};

export default ThemeContext;
